'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';
import { checkMintStatus } from '../utils/minting/check-mint-status';
import { dkgOptions } from '../config/dkg-options';

// Get all articles
export function useGetAllArticles(filters = {}) {
  return useQuery({
    queryKey: ['articles', filters],
    queryFn: async () => {
      const params = {};
      if (filters.topic) params.topic = filters.topic;
      if (filters.limit) params.limit = filters.limit;
      const res = await axios.get(API_ENDPOINTS.ARTICLES.GET_ALL, { params });
      let articles =
        res.data && Array.isArray(res.data.particles)
          ? res.data.particles
          : Array.isArray(res.data)
          ? res.data
          : [];
      if (filters.topic) {
        articles = articles.filter((a) =>
          a.topics?.some((t) =>
            typeof t === 'string'
              ? t.toLowerCase() === filters.topic.toLowerCase()
              : t.slug?.toLowerCase() === filters.topic.toLowerCase()
          )
        );
      }
      if (filters.limit && filters.limit > 0) {
        articles = articles.slice(0, filters.limit);
      }
      // Unwrap particle if present in each article
      articles = articles.map((article) => {
        const realArticle = article.particle ? article.particle : article;
        return {
          ...realArticle,
          creator: realArticle.creator || null,
        };
      });
      return articles;
    },
  });
}

// Get article by ID
export function useGetArticleById(articleId) {
  return useQuery({
    queryKey: ['articles', 'article', articleId],
    queryFn: async () => {
      if (!articleId) return null;
      const res = await axios.get(API_ENDPOINTS.ARTICLES.GET_ALL);
      const articles =
        res.data && Array.isArray(res.data.particles)
          ? res.data.particles
          : Array.isArray(res.data)
          ? res.data
          : [];
      const found = articles.find((a) => String(a.oid) === String(articleId));
      if (!found) throw new Error('Article not found');
      return { ...found, creator: found.creator || null };
    },
    enabled: !!articleId,
    staleTime: 1000 * 60 * 5,
  });
}

// Like article mutation (with optimistic update)
export function useLikeArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, uOID, creator }) => {
      if (!articleId || !uOID) {
        throw new Error(
          'articleId and user ID are required for liking an article'
        );
      }

      // Ensure articleId is a string
      if (typeof articleId !== 'string') {
        console.error(
          '[LikeArticle] Invalid articleId type:',
          typeof articleId,
          'Value:',
          articleId
        );
        throw new Error('Article ID must be a string');
      }

      const res = await axios.post(API_ENDPOINTS.ARTICLES.LIKE(articleId), {
        sOID: articleId,
        uOID,
        interactionType: 'like',
        creator,
      });
      return res.data;
    },
    onMutate: async ({ articleId }) => {
      await queryClient.cancelQueries();
      // Optimistically update single article
      const previousArticle = queryClient.getQueryData([
        'articles',
        'article',
        articleId,
      ]);
      if (previousArticle) {
        queryClient.setQueryData(['articles', 'article', articleId], {
          ...previousArticle,
          likes: (previousArticle.likes || 0) + 1,
        });
      }
      // Optimistically update all articles lists
      const queries = queryClient.getQueriesData({
        queryKey: ['articles'],
      });
      const previousArticlesByKey = {};
      queries.forEach(([key, previousArticles]) => {
        if (Array.isArray(previousArticles)) {
          previousArticlesByKey[JSON.stringify(key)] = previousArticles;
          queryClient.setQueryData(
            key,
            previousArticles.map((a) =>
              a.oid === articleId ? { ...a, likes: (a.likes || 0) + 1 } : a
            )
          );
        }
      });
      return { previousArticle, previousArticlesByKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousArticle) {
        queryClient.setQueryData(
          ['articles', 'article', variables.articleId],
          context.previousArticle
        );
      }
      if (context?.previousArticlesByKey) {
        Object.entries(context.previousArticlesByKey).forEach(([key, data]) => {
          queryClient.setQueryData(JSON.parse(key), data);
        });
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['articles', 'article', variables.articleId],
      });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

// Dislike article mutation (with optimistic update)
export function useDislikeArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, uOID, creator }) => {
      const res = await axios.post(API_ENDPOINTS.ARTICLES.DISLIKE(articleId), {
        sOID: articleId,
        uOID,
        interactionType: 'dislike',
        creator,
      });
      return res.data;
    },
    onMutate: async ({ articleId }) => {
      await queryClient.cancelQueries();
      // Optimistically update single article
      const previousArticle = queryClient.getQueryData([
        'articles',
        'article',
        articleId,
      ]);
      if (previousArticle) {
        queryClient.setQueryData(['articles', 'article', articleId], {
          ...previousArticle,
          dislikes: (previousArticle.dislikes || 0) + 1,
        });
      }
      // Optimistically update all articles lists
      const queries = queryClient.getQueriesData({
        queryKey: ['articles'],
      });
      const previousArticlesByKey = {};
      queries.forEach(([key, previousArticles]) => {
        if (Array.isArray(previousArticles)) {
          previousArticlesByKey[JSON.stringify(key)] = previousArticles;
          queryClient.setQueryData(
            key,
            previousArticles.map((a) =>
              a.oid === articleId
                ? { ...a, dislikes: (a.dislikes || 0) + 1 }
                : a
            )
          );
        }
      });
      return { previousArticle, previousArticlesByKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousArticle) {
        queryClient.setQueryData(
          ['articles', 'article', variables.articleId],
          context.previousArticle
        );
      }
      if (context?.previousArticlesByKey) {
        Object.entries(context.previousArticlesByKey).forEach(([key, data]) => {
          queryClient.setQueryData(JSON.parse(key), data);
        });
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['articles', 'article', variables.articleId],
      });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

// Create article mutation
export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (articleData) => {
      // Attach dkgOptions for minting
      let particle = { ...articleData };
      particle = { particle, options: dkgOptions }; // Ensure it matches API structure

      const res = await axios.post(API_ENDPOINTS.ARTICLES.CREATE, particle);
      // Check for jobID and poll mint status
      if (res.data && res.data.success && res.data.jobID) {
        const mintStatus = await checkMintStatus(
          res.data.jobID,
          4000, // 4 seconds
          250
        );
        if (!mintStatus || !mintStatus.success) {
          console.error('[CreateArticle] Minting failed:', mintStatus?.error);
          throw new Error(
            mintStatus?.error || 'Failed to mint article NFT. Please try again.'
          );
        }
        return {
          ...res.data,
          mintStatus: mintStatus.state,
          mintSuccess: mintStatus.success,
          error: mintStatus.error || null,
        };
      } else {
        console.error('[CreateArticle] No jobID returned:', res.data);
        throw new Error('Failed to create article. No job ID returned.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

// Get articles by author
export function useArticlesByCreator(creator) {
  return useQuery({
    queryKey: ['articles', 'creator', creator],
    queryFn: async () => {
      const response = await axios.get(
        API_ENDPOINTS.ARTICLES.GET_BY_CREATOR(creator)
      );
      // Optionally unwrap .particles if present
      if (response.data && Array.isArray(response.data.particles)) {
        return response.data.particles;
      }
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!creator,
  });
}

// By topic (backward compatibility)
export function useArticlesByTopic(topicSlug) {
  return useGetAllArticles({ topic: topicSlug });
}

// Main hook for compatibility
export function useArticles(articleId) {
  const articleQuery = useGetArticleById(articleId);
  const articlesQuery = useGetAllArticles();
  const likeArticle = useLikeArticle();
  return {
    article: articleQuery.data,
    articles: articlesQuery.data,
    isLoading: articleId ? articleQuery.isLoading : articlesQuery.isLoading,
    error: articleQuery.error || articlesQuery.error,
    likeArticle,
    isArticlesLoading: articlesQuery.isLoading,
  };
}

// All articles hook for compatibility
export function useAllArticles(options = {}) {
  const { data, isLoading, error } = useGetAllArticles(options);
  return { articles: data, isLoading, error };
}
